import { z } from 'zod';
import { severitySchema } from './common.js';
import { incidentSummarySchema } from './incidents.js';
/**
 * The single payload behind the NOC dashboard.
 *
 * One call, one WebSocket topic. Fifteen widgets making fifteen requests every
 * few seconds is how a dashboard becomes the heaviest client of its own API —
 * and how it stops updating at exactly the moment an outage makes everything
 * else slow.
 */
export const statusRollupSchema = z.object({
    total: z.number().int(),
    up: z.number().int(),
    down: z.number().int(),
    degraded: z.number().int(),
    maintenance: z.number().int(),
    /** Never folded into `up`. "Not polled" is not "healthy". */
    unknown: z.number().int(),
});
export const healthScoreSchema = z.object({
    score: z.number().min(0).max(100),
    /** Change vs. 24 hours ago, in points. */
    delta24h: z.number(),
    /**
     * The weighted inputs, exposed so the number can be explained in-product.
     * An opaque score is an ignored score.
     */
    components: z.object({
        deviceAvailability: z.object({ value: z.number(), weight: z.number() }),
        subscriberAvailability: z.object({ value: z.number(), weight: z.number() }),
        backboneQuality: z.object({ value: z.number(), weight: z.number() }),
        alarmPressure: z.object({ value: z.number(), weight: z.number() }),
    }),
    trend: z.array(z.object({ t: z.coerce.date(), v: z.number() })),
});
export const nocSummarySchema = z.object({
    generatedAt: z.coerce.date(),
    health: healthScoreSchema,
    alarms: z.object({
        bySeverity: z.record(severitySchema, z.number().int()),
        total: z.number().int(),
        unacknowledged: z.number().int(),
        /** How many were merged away by correlation — the system showing its work. */
        suppressed: z.number().int(),
    }),
    incidents: z.object({
        open: z.number().int(),
        bySeverity: z.record(severitySchema, z.number().int()),
        heads: z.array(incidentSummarySchema).max(10),
    }),
    infrastructure: z.object({
        olt: statusRollupSchema,
        pop: statusRollupSchema,
        tower: statusRollupSchema,
        core: statusRollupSchema,
        access: statusRollupSchema,
    }),
    subscribers: z.object({
        total: z.number().int(),
        online: z.number().int(),
        offline: z.number().int(),
        /** Offline and attributable to an open incident, as opposed to CPE off. */
        affectedByOutage: z.number().int(),
        unmapped: z.number().int(),
    }),
    internet: z.object({
        status: z.enum(['UP', 'DEGRADED', 'DOWN']),
        totalInBps: z.number(),
        totalOutBps: z.number(),
        utilisationPct: z.number(),
        latencyMs: z.number().nullable(),
        jitterMs: z.number().nullable(),
        lossPct: z.number().nullable(),
        upstreams: z.array(z.object({
            name: z.string(),
            status: z.enum(['UP', 'DEGRADED', 'DOWN']),
            inBps: z.number(),
            outBps: z.number(),
            utilisationPct: z.number(),
        })),
    }),
    power: z.object({
        onGrid: z.number().int(),
        onBattery: z.number().int(),
        onGenerator: z.number().int(),
        lowBattery: z.number().int(),
    }),
    sla: z.object({
        monthToDatePct: z.number(),
        targetPct: z.number(),
        /** Remaining error budget as a percentage of the month's allowance. */
        budgetRemainingPct: z.number(),
        breachedIncidents: z.number().int(),
    }),
    traffic: z.array(z.object({ t: z.coerce.date(), inBps: z.number().nullable(), outBps: z.number().nullable() })),
});
export const eventFeedItemSchema = z.object({
    time: z.coerce.date(),
    severity: z.enum(['P1', 'P2', 'P3', 'P4', 'INFO']),
    source: z.string(),
    eventType: z.string(),
    message: z.string(),
    elementId: z.string().uuid().nullable(),
    elementLabel: z.string().nullable(),
    incidentId: z.string().uuid().nullable(),
});
export const weatherSchema = z.object({
    location: z.string(),
    temperatureC: z.number(),
    condition: z.string(),
    precipitationMm: z.number(),
    windKph: z.number(),
    /**
     * PAGASA storm signal when one is raised. Included because it is genuinely
     * operational here: a signal number is the strongest available predictor of
     * the next 24 hours of fiber and power incidents in Bulacan.
     */
    stormSignal: z.number().int().min(0).max(5).nullable(),
    alerts: z.array(z.string()),
});
//# sourceMappingURL=noc.js.map